import { useEffect, useState } from "react";
import { Check, X, ChevronDown, ChevronUp, ImageIcon } from "lucide-react";
import CheckBox from "../checkbox/checkbox";
import { useGetAnswerChoiceImageQuery } from "../../api/questionsSlice/questionsSlice";
import styles from "./multipleChoiceAnswerBlock.module.scss";
import { processQuestionLabel } from "../../helpers/testHelpers";
function AnswerChoiceImage({ answerSerial }: { answerSerial: number }) {
  const [isOpen, setIsOpen] = useState(true);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const {
    data: imageBlob,
    isLoading,
    isError,
  } = useGetAnswerChoiceImageQuery(
    { answer_serial: answerSerial },
    // Only start fetching once the panel is opened for the first time
    { skip: !isOpen },
  );

  useEffect(() => {
    if (!imageBlob) return;

    const url = URL.createObjectURL(imageBlob);
    setObjectUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [imageBlob]);

  return (
    <div className={styles.imageWrap}>
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={styles.imageToggle}
        aria-expanded={isOpen}
      >
        <ImageIcon size={13} />
        <span>{isOpen ? "Hide image" : "View image"}</span>
        {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>

      {/* Collapsible panel */}
      {isOpen && (
        <div className={styles.imagePanel}>
          {isLoading && (
            <div className={styles.imageStatus}>Loading image…</div>
          )}
          {isError && (
            <div className={styles.imageError}>Failed to load image.</div>
          )}
          {objectUrl && !isLoading && !isError && (
            <img
              src={objectUrl}
              alt={`Answer choice image for serial ${answerSerial}`}
              className={styles.answerImage}
              onLoad={() => {
                // objectUrl is already revoked in the cleanup, so nothing extra needed here
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MultipleChoiceAnswerBlock
// ---------------------------------------------------------------------------
function MultipleChoiceAnswerBlock({
  processQuestionText,
  GetShuffledAnswerArray,
  currentQuestion,
  completedAnswerArray,
  examinationState,
  createdExam,
  handleAnswerChecked,
  getHighlightedText,
  showChecksAndXes,
  showExplanations,
  DLOSCEMode,
}) {
  // Only show "% chosen" once at least one user has answered this question
  const hasChoiceData =
    currentQuestion?.AnswerStats?.some(
      (stat: { percent_chosen: number | null }) =>
        (stat.percent_chosen ?? 0) > 0,
    ) ?? false;

  return (
    <div className={styles.block}>
      <span className={styles.questionText}>
        {processQuestionText(currentQuestion.GetQuestion.question_text)}
      </span>
      <div>
        {GetShuffledAnswerArray().map((answer, index) => {
          const answerDataId = `answerChoice-${answer?.answer_serial}`;
          // % of users who picked this choice (from AnswerStats returned by getquestiononly)
          const percentChosen = currentQuestion?.AnswerStats?.find(
            (stat: { answer_serial: number; percent_chosen: number | null }) =>
              stat.answer_serial === answer?.answer_serial,
          )?.percent_chosen;
          const hasAnswerImage =
            answer?.answer_choice_image != null &&
            answer?.answer_choice_image !== "";

          return (
            <div
              className={styles.answer}
              key={`answer.answer_serial ${index}`}
            >
              <span className={styles.answerRow} key={answer.answer_serial}>
                <span data-id={answerDataId} className={styles.answerInner}>
                  <div className={styles.answerMarker}>
                    <CheckBox
                      checked={
                        completedAnswerArray == undefined
                          ? examinationState.answersById[
                              createdExam.QuestionsForTest[
                                examinationState.currentQuestionIndex
                              ]
                            ]?.includes(answer?.answer_serial)
                          : completedAnswerArray.includes(answer?.answer_serial)
                      }
                      setChecked={() => {
                        handleAnswerChecked(answer.answer_serial);
                      }}
                      text={""}
                    />
                  </div>
                  {DLOSCEMode == "DLOSCE" && (
                    <div className={styles.answerMarker}>
                      <span>{processQuestionLabel(index + 1)}</span>
                    </div>
                  )}
                  <span data-id="container-break" className={styles.answerText}>
                    {getHighlightedText(answer?.answer_choice, answerDataId)}
                    {showChecksAndXes && answer.is_neutral && (
                      <span
                        title="Neutral answer: no points gained or lost"
                        style={{
                          color: "#9ca3af",
                          fontWeight: 700,
                          marginLeft: 6,
                        }}
                      >
                        –
                      </span>
                    )}
                    {showChecksAndXes && answer.is_correct && (
                      <Check className={styles.correctIcon} />
                    )}
                    {showChecksAndXes &&
                      completedAnswerArray != undefined &&
                      !answer.is_correct &&
                      !answer.is_neutral &&
                      completedAnswerArray.includes(answer?.answer_serial) && (
                        <X className={styles.wrongIcon} />
                      )}
                    {showExplanations &&
                      hasChoiceData &&
                      percentChosen != null && (
                        <span className={styles.percentChosen}>
                          ({percentChosen}%)
                        </span>
                      )}
                  </span>
                </span>
              </span>

              {/* Per-answer image — only shown when the answer has an associated image */}
              {hasAnswerImage && (
                <div className={styles.answerImageIndent}>
                  <AnswerChoiceImage answerSerial={answer.answer_serial} />
                </div>
              )}

              {showExplanations && (
                <span className={styles.explanation}>{answer.explanation}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MultipleChoiceAnswerBlock;